import { memo, useCallback, useMemo } from 'react';

import { type IconComponent, LayersIcon, MessageSquareIcon } from '@air/design-icons';

import { PublicAssetMobileNavigationEllipsisMenu } from '~/_app/(public-asset)/_components/PublicAssetMobileNavigationEllipsisMenu';
import { MobileAssetModalNavigation } from '~/_components/MobileAssetModalNavigation';
import { MobileAssetModalNavigationItem } from '~/_components/MobileAssetModalNavigationItem';
import { PublicAssetMobileDownloadButton } from '~/components/PublicAssetModal/components/PublicAssetMobileDownloadButton';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';
import { AssetModalPanel, useAssetModalPanelContext } from '~/providers/AssetModalPanelContextProvider';
import { canDownloadVersion, canViewAssetDiscussions } from '~/utils/permissions/assetPermissions';

type PublicMobileNavigationItemModel = {
  id: AssetModalPanel;
  icon: IconComponent;
  label: string;
};

type PublicMobileNavigationItemProps = {
  isActive: boolean;
  item: PublicMobileNavigationItemModel;
  onSelect: (panel: AssetModalPanel) => void;
};

const PublicMobileNavigationItem = memo(({ isActive, item, onSelect }: PublicMobileNavigationItemProps) => {
  const onClick = useCallback(() => {
    onSelect(item.id);
  }, [item.id, onSelect]);

  return (
    <MobileAssetModalNavigationItem
      adsIcon
      icon={item.icon}
      id={item.id}
      isActive={isActive}
      label={item.label}
      onClick={onClick}
    />
  );
});

PublicMobileNavigationItem.displayName = 'PublicMobileNavigationItem';

export const PublicMobileAssetModalNavigation = memo(() => {
  const { panel, togglePanel } = useAssetModalPanelContext();
  const { permissions } = usePublicPermissions();

  const canViewDiscussions = canViewAssetDiscussions(permissions);
  const { canViewAllVersions } = useCanViewAllVersionsOnPublicShareLink();
  const canDownload = canDownloadVersion(permissions);

  const navigationItems = useMemo(() => {
    const navigationItemsArray: PublicMobileNavigationItemModel[] = [];

    if (canViewDiscussions) {
      navigationItemsArray.push({
        id: AssetModalPanel.COMMENTS,
        icon: MessageSquareIcon,
        label: 'Comments',
      });
    }

    if (canViewAllVersions) {
      navigationItemsArray.push({
        id: AssetModalPanel.VERSIONS,
        icon: LayersIcon,
        label: 'Version',
      });
    }

    return navigationItemsArray;
  }, [canViewAllVersions, canViewDiscussions]);

  const onSelect = useCallback(
    (nextPanel: AssetModalPanel) => {
      togglePanel(nextPanel);

      // @TODO - Future change
      // Scroll to position when panel is changed
      // if (panel === AssetModalPanel.COMMENTS) {
      //   scrollToBottom();
      // } else {
      //   scrollToPanel();
      // }
    },
    [togglePanel],
  );

  return (
    <MobileAssetModalNavigation suffix={canDownload ? <PublicAssetMobileDownloadButton /> : undefined}>
      {navigationItems.map((item) => (
        <PublicMobileNavigationItem isActive={panel === item.id} item={item} key={item.id} onSelect={onSelect} />
      ))}

      <PublicAssetMobileNavigationEllipsisMenu />
    </MobileAssetModalNavigation>
  );
});

PublicMobileAssetModalNavigation.displayName = 'PublicMobileAssetModalNavigation';
